import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell
} from 'recharts';
import { Card } from "@/components/ui/card";

interface TasksByDay {
  [key: string]: number;
}

interface TasksByPriority {
  critical: number;
  important: number;
  normal: number;
}

interface AnalyticsGraphsProps {
  tasksByDay: TasksByDay;
  tasksByPriority: TasksByPriority;
  completionRate: number;
}

export function AnalyticsGraphs({ tasksByDay, tasksByPriority, completionRate: _completionRate }: AnalyticsGraphsProps) {
  // Process tasks by day data for the area chart
  const taskTrendData = Object.entries(tasksByDay)
    .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      tasks: count
    }))
    .slice(-14); // Show last 14 days

  // Process priority data for the bar chart
  const priorityData = [
    { name: 'Critical', value: tasksByPriority.critical, color: '#ef4444' },
    { name: 'Important', value: tasksByPriority.important, color: '#f59e0b' },
    { name: 'Normal', value: tasksByPriority.normal, color: '#3b82f6' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {/* Task Trend Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Task Trend (Last 14 Days)</h3>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={taskTrendData}>
              <defs>
                <linearGradient id="taskGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af' }}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '0.375rem'
                }}
                labelStyle={{ color: '#9ca3af' }}
                itemStyle={{ color: '#9ca3af' }}
              />
              <Area
                type="monotone"
                dataKey="tasks"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#taskGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Priority Distribution Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Task Priority Distribution</h3>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={priorityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="name"
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af' }}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '0.375rem'
                }}
                labelStyle={{ color: '#9ca3af' }}
                itemStyle={{ color: '#9ca3af' }}
              />
              <Legend />
              <Bar
                dataKey="value"
                name="Tasks"
                fill="#3b82f6"
              >
                {priorityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
} 